import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, KeyRound, Smartphone, Copy, Download, RefreshCw, Database, Upload, RotateCcw, AlertTriangle, Lock, Vote, Trash2, FileSpreadsheet } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useElectionDay } from "@/context/ElectionDayContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function Settings() {  const { user, refreshUser } = useAuth();
  const [setup, setSetup] = useState(null); // {qr_code, secret}
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null); // string[] shown once
  const [regen, setRegen] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const isSuperAdmin = user.role === "super_admin";
  const [autoBackups, setAutoBackups] = useState([]);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadBackups = async () => {
    try {
      const { data } = await api.get("/admin/backups");
      setAutoBackups(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (isSuperAdmin) loadBackups(); }, [isSuperAdmin]);

  const [xlsxLog, setXlsxLog] = useState([]);
  const loadXlsxLog = async () => {
    try {
      const { data } = await api.get("/admin/backup/xlsx/log");
      setXlsxLog(data.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (user.is_owner) loadXlsxLog(); }, [user.is_owner]);

  const [statusLock, setStatusLock] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);

  const { ed, refreshEd } = useElectionDay();
  const [edForm, setEdForm] = useState({ freeze_at: "", tally_open_at: "", test_mode: false, test_all_users: false, duty_hours: "", emergency_contact: "", scoreboard_access: "gm" });
  const [edBusy, setEdBusy] = useState(false);
  const [predChanges, setPredChanges] = useState(null);
  useEffect(() => {
    if (!ed) return;
    setEdForm({
      freeze_at: String(ed.freeze_at || "").slice(0, 16),
      tally_open_at: String(ed.tally_open_at || "").slice(0, 16),
      test_mode: !!ed.test_mode,
      test_all_users: !!ed.test_all_users,
      duty_hours: ed.duty_hours || "",
      emergency_contact: ed.emergency_contact || "",
      scoreboard_access: ed.scoreboard_access || "gm",
    });
  }, [ed]);

  const saveElectionDay = async () => {
    setEdBusy(true);
    try {
      await api.put("/settings/election-day", {
        freeze_at: `${edForm.freeze_at}:00+03:00`,
        tally_open_at: `${edForm.tally_open_at}:00+03:00`,
        test_mode: edForm.test_mode,
        test_all_users: edForm.test_all_users,
        duty_hours: edForm.duty_hours,
        emergency_contact: edForm.emergency_contact,
        scoreboard_access: edForm.scoreboard_access,
      });
      await refreshEd();
      toast.success("Seçim günü ayarları kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setEdBusy(false);
    }
  };

  const watchUrl = ed?.scoreboard_token
    ? `${window.location.origin}/izle?token=${ed.scoreboard_token}`
    : "";

  const makeWatchLink = async () => {
    setEdBusy(true);
    try {
      await api.post("/election-day/scoreboard-link");
      await refreshEd();
      toast.success("İzleme bağlantısı oluşturuldu");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setEdBusy(false);
    }
  };

  const revokeWatchLink = async () => {
    if (!window.confirm("İzleme bağlantısı iptal edilecek, link çalışmayacak. Onaylıyor musunuz?")) return;
    setEdBusy(true);
    try {
      await api.delete("/election-day/scoreboard-link");
      await refreshEd();
      toast.success("İzleme bağlantısı iptal edildi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setEdBusy(false);
    }
  };

  const loadPredChanges = async () => {
    setEdBusy(true);
    try {
      const { data } = await api.get("/election-day/prediction-changes");
      setPredChanges(data);
      toast.success(data.needs_revert
        ? `${data.needs_revert} firmanın eğilimi çeteleden değiştirilmiş`
        : "Çetele kaynaklı değişiklik bulunamadı — komite verileri temiz");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setEdBusy(false);
    }
  };

  const revertPredChanges = async () => {
    if (!window.confirm(`${predChanges?.needs_revert} firmanın oy eğilimi, çeteleden değiştirilmeden önceki ilk haline döndürülecek. Onaylıyor musunuz?`)) return;
    setEdBusy(true);
    try {
      const { data } = await api.post("/election-day/prediction-changes/revert", {});
      toast.success(`${data.reverted} firma eski haline döndürüldü`);
      const { data: fresh } = await api.get("/election-day/prediction-changes");
      setPredChanges(fresh);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setEdBusy(false);
    }
  };

  const purgeAnnouncements = async () => {
    if (!window.confirm("Seçim günü duyuruları (komite sonucu ve prova bildirimleri) TÜM kullanıcıların gelen kutusundan silinecek. Onaylıyor musunuz?")) return;
    setEdBusy(true);
    try {
      const { data } = await api.delete("/election-day/announcements");
      toast.success(data.deleted ? `${data.deleted} duyuru mesajı silindi` : "Silinecek duyuru bulunamadı");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setEdBusy(false);
    }
  };

  const resetTally = async () => {    if (!window.confirm("Test modunda girilen tüm çetele işaretlemeleri silinecek. Onaylıyor musunuz?")) return;
    setEdBusy(true);
    try {
      const { data } = await api.post("/election-day/reset-test");
      await refreshEd();
      toast.success(`${data.deleted} test işaretlemesi silindi`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setEdBusy(false);
    }
  };
  useEffect(() => {
    api.get("/settings/status-lock").then(({ data }) => setStatusLock(data)).catch(() => {});
  }, []);
  const toggleStatusLock = async (v) => {
    setLockBusy(true);
    try {
      const { data } = await api.put("/settings/status-lock", { locked: v });
      setStatusLock(data);
      toast.success(v ? "Durum kilidi açıldı — ASKI/FAAL değiştirilemez" : "Durum kilidi kapatıldı");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLockBusy(false);
    }
  };

  const downloadBackup = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `secimburosu_tam_yedek_${new Date().toISOString().slice(0, 10)}.json.gz`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Tam yedek indirildi (tüm koleksiyonlar, sıkıştırılmış)");
    } catch {
      toast.error("Yedek indirilemedi");
    } finally { setBusy(false); }
  };

  const downloadExcelBackup = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup/xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `secimburosu_tam_excel_yedek_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Tam Excel yedek indirildi (her veri türü ayrı alt sayfada)");
      loadXlsxLog();
    } catch {
      toast.error("Excel yedek indirilemedi");
    } finally { setBusy(false); }
  };

  const backupNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backup/now");
      toast.success("Yeni otomatik yedek alındı");
      await loadBackups();
    } catch {
      toast.error("Yedekleme başarısız");
    } finally { setBusy(false); }
  };

  const previewRestore = async () => {
    if (!restoreFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/admin/restore/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setRestorePreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Yedek dosyası okunamadı");
    } finally { setBusy(false); }
  };

  const doRestoreFile = async () => {
    if (!restoreFile) return;
    if (!window.confirm("Yedekteki koleksiyonlar tamamen değiştirilecek (mevcut kayıtlar silinecek). Onaylıyor musunuz?")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = Object.keys(data.restored || {}).length;
      toast.success(`Geri yüklendi: ${n} koleksiyon · ${data.restored.voters ?? 0} firma, ${data.restored.users ?? 0} kullanıcı`);
      setRestoreFile(null);
      setRestorePreview(null);
      if (fileRef.current) fileRef.current.value = "";
      await loadBackups();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Geri yükleme başarısız");
    } finally { setBusy(false); }
  };

  const restoreAuto = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/backups/${id}/restore`);
      toast.success(`Geri yüklendi: ${data.restored.voters} firma`);
    } catch {
      toast.error("Geri yükleme başarısız");
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText((backupCodes || []).join("\n"));
    toast.success("Kodlar panoya kopyalandı");
  };
  const downloadCodes = () => {
    const blob = new Blob([
      `Secim Burosu - Yedek Kurtarma Kodlari\nHesap: ${user.email}\n\n` + (backupCodes || []).join("\n") +
      "\n\nHer kod yalnizca bir kez kullanilabilir. Guvenli bir yerde saklayin.",
    ], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "secimburosu-yedek-kodlar.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  const regenerate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate", { code: regenCode });
      setBackupCodes(data.backup_codes);
      setRegen(false);
      setRegenCode("");
      toast.success("Yeni yedek kodlar üretildi");
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const enable = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      toast.success("İki adımlı doğrulama etkinleştirildi");
      setSetup(null);
      setCode("");
      if (data.backup_codes) setBackupCodes(data.backup_codes);
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("İki adımlı doğrulama kapatıldı");
      setCode("");
      setBackupCodes(null);
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground">Ayarlar & Güvenlik</h1>
        <p className="text-sm text-muted-foreground">Hesap ve iki adımlı doğrulama yönetimi</p>
      </div>

      <Card className="p-5 border border-border">
        <h3 className="font-display font-semibold mb-4">Hesap Bilgileri</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><p className="text-muted-foreground">Ad Soyad</p><p className="font-medium">{user.name}</p></div>
          <div><p className="text-muted-foreground">E-posta</p><p className="font-medium">{user.email}</p></div>
          <div><p className="text-muted-foreground">Rol</p><p className="font-medium">{ROLE_LABELS[user.role]}</p></div>
          <div><p className="text-muted-foreground">Komite</p><p className="font-medium">{user.committee_no ? `#${user.committee_no}` : "Tümü"}</p></div>
        </div>
      </Card>

      <Card className="p-5 border border-border">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`h-10 w-10 rounded-lg grid place-items-center ${user.totp_enabled ? "bg-emerald-100 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
              {user.totp_enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
            </div>
            <div>
              <h3 className="font-display font-semibold">İki Adımlı Doğrulama (2FA)</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-0.5">
                Google Authenticator uyumlu. Hesabınızı ekstra bir doğrulama katmanıyla koruyun.
              </p>
              <p className={`text-xs font-medium mt-1 ${user.totp_enabled ? "text-emerald-600" : "text-amber-600"}`}>
                Durum: {user.totp_enabled ? "Etkin" : "Pasif"}
              </p>
            </div>
          </div>
          <Switch checked={!!user.totp_enabled} disabled={loading}
            onCheckedChange={(v) => { if (v) startSetup(); else setSetup("disable"); }}
            data-testid="twofa-switch" />
        </div>

        {setup && setup !== "disable" && (
          <div className="mt-6 border-t border-border pt-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium"><Smartphone className="h-4 w-4" /> Kurulum</div>
            <p className="text-sm text-muted-foreground">1. Authenticator uygulamanızla QR kodu okutun (veya anahtarı elle girin).</p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <img src={setup.qr_code} alt="QR" className="h-40 w-40 rounded-lg border border-border" data-testid="twofa-qr" />
              <div className="text-xs bg-muted rounded-lg p-3 font-mono break-all">{setup.secret}</div>
            </div>
            <p className="text-sm text-muted-foreground">2. Uygulamadaki 6 haneli kodu girin:</p>
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="123456" className="max-w-[160px] text-center tracking-widest font-mono" data-testid="twofa-enable-code" />
              <Button onClick={enable} disabled={loading || code.length !== 6} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="twofa-enable-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-2" />Etkinleştir</>}
              </Button>
            </div>
          </div>
        )}

        {setup === "disable" && (
          <div className="mt-6 border-t border-border pt-6 space-y-3">
            <p className="text-sm text-muted-foreground">2FA'yı kapatmak için mevcut doğrulama kodunuzu girin:</p>
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="123456" className="max-w-[160px] text-center tracking-widest font-mono" data-testid="twofa-disable-code" />
              <Button variant="destructive" onClick={disable} disabled={loading || code.length !== 6} data-testid="twofa-disable-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kapat"}
              </Button>
              <Button variant="ghost" onClick={() => { setSetup(null); setCode(""); }}>Vazgeç</Button>
            </div>
          </div>
        )}

        {backupCodes && (
          <div className="mt-6 border-t border-border pt-6 space-y-3" data-testid="backup-codes-block">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600"><KeyRound className="h-4 w-4" /> Yedek Kurtarma Kodları</div>
            <p className="text-sm text-muted-foreground">
              Telefonunuza erişemezseniz bu kodlarla giriş yapabilirsiniz. <span className="font-medium text-foreground">Her kod yalnızca bir kez kullanılır.</span> Şimdi kaydedin — bu kodlar bir daha gösterilmeyecek.
            </p>
            <div className="grid grid-cols-2 gap-2 bg-muted rounded-lg p-4 font-mono text-sm" data-testid="backup-codes-list">
              {backupCodes.map((c) => <div key={c} className="tracking-widest text-center">{c}</div>)}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyCodes} data-testid="backup-copy-button"><Copy className="h-4 w-4 mr-2" />Kopyala</Button>
              <Button variant="outline" size="sm" onClick={downloadCodes} data-testid="backup-download-button"><Download className="h-4 w-4 mr-2" />İndir</Button>
              <Button variant="ghost" size="sm" onClick={() => setBackupCodes(null)} data-testid="backup-dismiss-button">Kaydettim, kapat</Button>
            </div>
          </div>
        )}

        {user.totp_enabled && !backupCodes && (
          <div className="mt-6 border-t border-border pt-6 space-y-3" data-testid="backup-manage-block">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Yedek kurtarma kodları</p>
                <p className="text-xs text-muted-foreground">Kalan kullanılabilir kod: <span className="font-medium text-foreground">{user.backup_codes_remaining ?? 0}</span></p>
              </div>
              {!regen && (
                <Button variant="outline" size="sm" onClick={() => setRegen(true)} data-testid="backup-regen-open"><RefreshCw className="h-4 w-4 mr-2" />Yeni kod üret</Button>
              )}
            </div>
            {regen && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Yeni kodlar eskilerini geçersiz kılar. Onaylamak için uygulamadaki 6 haneli kodu girin:</p>
                <div className="flex gap-2">
                  <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="123456" className="max-w-[160px] text-center tracking-widest font-mono" data-testid="backup-regen-code" />
                  <Button onClick={regenerate} disabled={loading || regenCode.length !== 6} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="backup-regen-confirm">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Üret"}
                  </Button>
                  <Button variant="ghost" onClick={() => { setRegen(false); setRegenCode(""); }}>Vazgeç</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {isSuperAdmin && (
        <Card className="p-5 border border-border" data-testid="election-day-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg grid place-items-center bg-[#b3122b]/10 text-[#b3122b]">
              <Vote className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display font-semibold">Seçim Günü (Donma & Çetele)</h3>
              <p className="text-sm text-muted-foreground max-w-xl mt-0.5">
                Donma saatinden sonra Genel Yöneticiler dışında hiç kimse veri girişi/değişiklik
                yapamaz. Çetele saati geldiğinde ana ekranda büyük düğme canlıya geçer ve test
                modunda girilen işaretlemeler otomatik sıfırlanır.
              </p>
              {ed?.updated_by && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="ed-meta">
                  Son değişiklik: {ed.updated_by} · {String(ed.updated_at || "").slice(0, 16).replace("T", " ")}
                </p>
              )}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Donma saati (salt okunur başlangıcı)</Label>
              <Input type="datetime-local" value={edForm.freeze_at}
                onChange={(e) => setEdForm({ ...edForm, freeze_at: e.target.value })}
                data-testid="ed-freeze-at" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Çetele açılış saati (canlı)</Label>
              <Input type="datetime-local" value={edForm.tally_open_at}
                onChange={(e) => setEdForm({ ...edForm, tally_open_at: e.target.value })}
                data-testid="ed-tally-open-at" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Görev saati (görevli kartlarında yazar)</Label>
              <Input value={edForm.duty_hours} placeholder="11 Ekim 2026 Pazar · 08:30 – 17:00"
                onChange={(e) => setEdForm({ ...edForm, duty_hours: e.target.value })}
                data-testid="ed-duty-hours" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Seçim günü acil irtibat</Label>
              <Input value={edForm.emergency_contact} placeholder="Seçim Merkezi 0212 ..."
                onChange={(e) => setEdForm({ ...edForm, emergency_contact: e.target.value })}
                data-testid="ed-emergency-contact" />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-semibold">Test modu (çetele şimdiden açık)</p>
                <p className="text-xs text-muted-foreground">Açılış saatini beklemeden çeteleyi deneyin.</p>
              </div>
              <Switch checked={!!edForm.test_mode} data-testid="ed-test-mode"
                onCheckedChange={(v) => setEdForm({ ...edForm, test_mode: v })} />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-semibold">Test modunu tüm kullanıcılara aç</p>
                <p className="text-xs text-muted-foreground">
                  Kapalıyken yalnız Genel Yöneticiler test çetelesini görür. Süreli alıştırma için
                  bunun yerine Seçim Çetelesi sayfasındaki <b>“Provayı Başlat”</b> düğmesini kullanın.
                </p>
              </div>
              <Switch checked={!!edForm.test_all_users} data-testid="ed-test-all-users"
                onCheckedChange={(v) => setEdForm({ ...edForm, test_all_users: v })} />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-semibold">Sonuç Ekranını kim görebilir?</p>
                <p className="text-xs text-muted-foreground">
                  Seçim gecesi büyük puntolu tam ekran sonuç görünümü. “Tümü” seçilirse giriş yapan
                  tüm kullanıcılar izleyebilir.
                </p>
              </div>
              <select value={edForm.scoreboard_access} data-testid="ed-scoreboard-access"
                onChange={(e) => setEdForm({ ...edForm, scoreboard_access: e.target.value })}
                className="h-9 shrink-0 rounded-md border border-border bg-background px-2 text-sm">
                <option value="gm">Sadece Genel Yönetici</option>
                <option value="all">Tümü</option>
              </select>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-sm font-semibold">Salondaki ekran için izleme bağlantısı</p>
              <p className="text-xs text-muted-foreground">
                Şifresiz, salt-izleme sonuç ekranı. Kişisel veri ve sandık görevlisi adı
                göstermez; sadece koltuk, katılım ve komite sonuçlarını yayınlar.
              </p>
              {watchUrl ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input readOnly value={watchUrl} className="h-9 max-w-md text-xs"
                    data-testid="ed-watch-url" onFocus={(e) => e.target.select()} />
                  <Button size="sm" variant="outline" data-testid="ed-watch-copy"
                    onClick={() => { navigator.clipboard?.writeText(watchUrl); toast.success("Bağlantı kopyalandı"); }}>
                    Kopyala
                  </Button>
                  <Button size="sm" variant="outline" onClick={makeWatchLink} disabled={edBusy}
                    data-testid="ed-watch-rotate">Yenile</Button>
                  <Button size="sm" variant="outline" onClick={revokeWatchLink} disabled={edBusy}
                    data-testid="ed-watch-revoke">İptal et</Button>
                </div>
              ) : (
                <Button size="sm" className="mt-2" onClick={makeWatchLink} disabled={edBusy}
                  data-testid="ed-watch-create">İzleme bağlantısı oluştur</Button>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={saveElectionDay} disabled={edBusy} data-testid="ed-save">
              {edBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Kaydet
            </Button>
            <Button variant="outline" onClick={resetTally} disabled={edBusy} data-testid="ed-reset">
              <RotateCcw className="h-4 w-4 mr-2" /> Test Çetelesini Sıfırla
            </Button>
            <Button variant="outline" onClick={purgeAnnouncements} disabled={edBusy}
              data-testid="ed-purge-announcements">
              <Trash2 className="h-4 w-4 mr-2 text-red-500" /> Seçim Günü Duyurularını Sil
            </Button>
            <Button variant="outline" onClick={loadPredChanges} disabled={edBusy}
              data-testid="ed-pred-check">
              <ShieldCheck className="h-4 w-4 mr-2" /> Çetele Komite Verisini Değiştirdi mi?
            </Button>
            {ed && (
              <span className="text-xs text-muted-foreground" data-testid="ed-status">
                Durum: {ed.frozen ? "DONMUŞ" : "açık"} · Çetele: {ed.tally_open ? (ed.tally_test ? "test modunda açık" : "CANLI") : "kapalı"} · {ed.marked_count ?? 0} işaretleme
              </span>
            )}
          </div>
          {predChanges && (
            <div className="mt-3 rounded-lg border border-border p-3" data-testid="ed-pred-panel">
              {predChanges.needs_revert === 0 ? (
                <p className="text-sm text-emerald-700 dark:text-emerald-400" data-testid="ed-pred-clean">
                  Komite verileri temiz — çetele ekranından değiştirilmiş ve hâlâ farklı duran
                  oy eğilimi kaydı yok{predChanges.total ? ` (geçmişte ${predChanges.total} değişiklik kaydı var, hepsi ilk haline dönmüş).` : "."}
                </p>
              ) : (
                <>
                  <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                    {predChanges.needs_revert} firmanın oy eğilimi çetele ekranından değiştirilmiş
                    ve ana kayıt hâlâ farklı.
                  </p>
                  <div className="mt-2 max-h-56 overflow-auto rounded border border-border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/60 text-muted-foreground">
                        <tr>
                          <th className="text-left px-2 py-1 font-medium">Firma</th>
                          <th className="text-left px-2 py-1 font-medium">K.</th>
                          <th className="text-left px-2 py-1 font-medium">İlk Hali</th>
                          <th className="text-left px-2 py-1 font-medium">Şu An</th>
                          <th className="text-left px-2 py-1 font-medium">Değiştiren</th>
                        </tr>
                      </thead>
                      <tbody>
                        {predChanges.rows.filter((r) => r.needs_revert).map((r) => (
                          <tr key={r.voter_id} className="border-t border-border/50" data-testid={`ed-pred-row-${r.voter_id}`}>
                            <td className="px-2 py-1">{r.company_name}</td>
                            <td className="px-2 py-1">{r.committee_no}</td>
                            <td className="px-2 py-1 font-semibold text-emerald-700 dark:text-emerald-400">{r.original || "—"}</td>
                            <td className="px-2 py-1 font-semibold text-red-600">{r.current || "—"}</td>
                            <td className="px-2 py-1 text-muted-foreground">{r.last_by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button size="sm" className="mt-2 bg-[#0F2C59] hover:bg-[#0F2C59]/90"
                    onClick={revertPredChanges} disabled={edBusy} data-testid="ed-pred-revert">
                    <RotateCcw className="h-4 w-4 mr-2" /> Tümünü eski haline çevir
                  </Button>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      {isSuperAdmin && (
        <Card className="p-5 border border-border" data-testid="status-lock-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg grid place-items-center bg-amber-500/10 text-amber-600">
              <Lock className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-display font-semibold">Durum Kilidi (ASKI / FAAL)</h3>
              <p className="text-sm text-muted-foreground max-w-xl mt-0.5">
                Kilit açıkken hiç kimse (Genel Yönetici dahil) firma durumunu değiştiremez; toplu
                durum aracı, Excel içe aktarma ve şube katmanı da durumu atlar. Kilidi yalnız
                buradan kapatabilirsiniz.
              </p>
              {statusLock?.updated_by && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="status-lock-meta">
                  Son değişiklik: {statusLock.updated_by} · {String(statusLock.updated_at || "").slice(0, 16).replace("T", " ")}
                </p>
              )}
            </div>
            <Switch checked={!!statusLock?.locked} disabled={lockBusy}
              onCheckedChange={toggleStatusLock} data-testid="status-lock-switch" />
          </div>
        </Card>
      )}

      {isSuperAdmin && (
        <Card className="p-5 border border-border" data-testid="backup-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg grid place-items-center bg-[#0F2C59]/10 text-[#0F2C59] dark:text-sky-300">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display font-semibold">Yedekleme & Geri Yükleme</h3>
              <p className="text-sm text-muted-foreground max-w-lg mt-0.5">
                Tüm veriyi (kullanıcılar, komiteler, firmalar, sorumlular, mesajlar, görevler, yetki
                belgesi takibi, seçim günü verileri, seyahat planı, ayarlar ve işlem günlüğü dahil
                <b> tüm koleksiyonlar</b>) tek dosyada yedekleyin. Dosya sıkıştırılmış (.json.gz)
                indirilir; başka bir kuruluma aynı dosyadan geri yüklenebilir.
                <b className="block mt-1">Tam Excel Yedek</b>
                <span className="text-amber-700 dark:text-amber-400"> (yalnız sistem sahibi)</span>: aynı verinin tek bir Excel dosyasında,
                her veri türü ayrı alt sayfada (ilk sayfa içindekiler) okunabilir hâli — sistem olmasa bile
                bilgilere Excel'den ulaşabilirsiniz. (Geri yükleme için .json.gz dosyasını kullanın.)
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadBackup} disabled={busy} data-testid="backup-download-json">
              <Download className="h-4 w-4 mr-2" /> Tam Yedek İndir (.json.gz)
            </Button>
            {user.is_owner && (
              <Button variant="outline" size="sm" onClick={downloadExcelBackup} disabled={busy} data-testid="backup-download-xlsx">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Tam Excel Yedek (.xlsx)
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={backupNow} disabled={busy} data-testid="backup-now-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Şimdi Yedekle
            </Button>
          </div>

          {user.is_owner && (
            <div className="mt-5 border-t border-border pt-4 space-y-2" data-testid="backup-xlsx-log">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileSpreadsheet className="h-4 w-4" /> Excel Yedek İndirme Kaydı
                <span className="text-xs font-normal text-muted-foreground">(son 20)</span>
              </div>
              {xlsxLog.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="backup-xlsx-log-empty">Henüz Excel yedek indirilmedi.</p>
              ) : (
                <div className="rounded-lg border border-border divide-y divide-border/60">
                  {xlsxLog.map((x, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-xs" data-testid={`backup-xlsx-log-row-${i}`}>
                      <span className="font-medium">{x.actor}</span>
                      <span className="text-muted-foreground">
                        {new Date(x.timestamp).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                      <span className="ml-auto text-muted-foreground tabular-nums">
                        {x.collections} veri türü · {(x.records || 0).toLocaleString("tr-TR")} kayıt
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 border-t border-border pt-5 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium"><Upload className="h-4 w-4" /> Yedekten Geri Yükle (dosya)</div>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json,.gz,application/gzip"
              onChange={(e) => { setRestoreFile(e.target.files?.[0] || null); setRestorePreview(null); }}
              className="block text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-muted file:text-foreground hover:file:bg-muted/70"
              data-testid="restore-file-input"
            />
            {restoreFile && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2" data-testid="restore-confirm-block">
                <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span><b>Dikkat:</b> Bu işlem mevcut tüm veriyi silip <b>{restoreFile.name}</b> içeriğiyle değiştirir. Geri alınamaz. Devam etmeden önce güncel bir yedek indirmeniz önerilir.</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={previewRestore} disabled={busy}
                    data-testid="restore-preview-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yedeği karşılaştır"}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={doRestoreFile} disabled={busy} data-testid="restore-confirm-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Evet, geri yükle"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setRestoreFile(null); setRestorePreview(null); if (fileRef.current) fileRef.current.value = ""; }}>Vazgeç</Button>
                </div>
                {restorePreview && (
                  <div className="rounded-md border border-border bg-background p-2" data-testid="restore-preview-table">
                    <div className="text-xs font-semibold mb-1">
                      Yedekte toplam {restorePreview.total_in_backup} kayıt · şu anda {restorePreview.total_current} kayıt
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="text-muted-foreground">
                          <tr className="text-left">
                            <th className="py-1 pr-3">Koleksiyon</th>
                            <th className="py-1 pr-3">Yedekte</th>
                            <th className="py-1 pr-3">Şu anda</th>
                          </tr>
                        </thead>
                        <tbody>
                          {restorePreview.rows.map((r) => (
                            <tr key={r.collection} className="border-t border-border">
                              <td className="py-1 pr-3">{r.collection}</td>
                              <td className="py-1 pr-3 font-semibold tabular-nums">{r.in_backup}</td>
                              <td className="py-1 pr-3 tabular-nums">{r.current}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {restorePreview.not_in_backup?.length > 0 && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                        Yedekte olmayan ve dokunulmayacak koleksiyonlar: {restorePreview.not_in_backup.join(", ")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {autoBackups.length > 0 && (
            <div className="mt-6 border-t border-border pt-5 space-y-2" data-testid="auto-backups-block">
              <div className="flex items-center gap-2 text-sm font-medium"><RotateCcw className="h-4 w-4" /> Otomatik Yedekler (son 30 gün)</div>
              <div className="max-h-56 overflow-y-auto divide-y divide-border rounded-lg border border-border">
                {autoBackups.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`auto-backup-${b.id}`}>
                    <div>
                      <div className="font-medium">{new Date(b.created_at).toLocaleString("tr-TR")}</div>
                      <div className="text-xs text-muted-foreground">{b.counts?.voters ?? 0} firma · {b.counts?.committees ?? 0} komite · {b.counts?.users ?? 0} kullanıcı</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => restoreAuto(b.id)} disabled={busy} data-testid={`auto-backup-restore-${b.id}`}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Geri Yükle
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
